import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { generateQuotePdf } from "@/lib/quotePdf";
import {
  ArrowLeft,
  Trash,
  FloppyDisk,
  FilePdf,
  Receipt,
  Storefront,
  User,
  CaretDown,
} from "@phosphor-icons/react";

const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

const emptyClient = { name: "", company: "", email: "", phone: "", gstin: "", address: "" };

function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function QuoteBuilder() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preIds = (params.get("ids") || "").split(",").filter(Boolean);

  const [lineItems, setLineItems] = useState([]);
  const [client, setClient] = useState(emptyClient);
  const [taxPercent, setTaxPercent] = useState(18);
  const [validUntil, setValidUntil] = useState(addDaysISO(15));
  const [notes, setNotes] = useState("");
  const [allServices, setAllServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/services");
        setAllServices(data);
        if (preIds.length) {
          const chosen = preIds
            .map((id) => data.find((s) => s.id === id))
            .filter(Boolean)
            .map((s) => svcToLine(s));
          setLineItems(chosen);
        }
      } catch {
        toast.error("Failed to load services");
      }
    })();
  }, []);

  const svcToLine = (svc) => ({
    _key: crypto.randomUUID(),
    service_id: svc.id,
    service_name: svc.service_name,
    category: svc.category,
    cities: Array.isArray(svc.cities) ? svc.cities : [],
    duration: svc.duration,
    moq: svc.moq,
    unit_price: Number(svc.price_per_unit || 0),
    quantity: 1,
    discount_percent: 0,
    images: Array.isArray(svc.images) ? [...svc.images] : [],
  });

  const totals = useMemo(() => {
    let subtotal = 0, discount = 0;
    for (const li of lineItems) {
      const gross = Number(li.unit_price || 0) * Number(li.quantity || 0);
      const disc = gross * (Number(li.discount_percent || 0) / 100);
      subtotal += gross;
      discount += disc;
    }
    const net = subtotal - discount;
    const taxAmt = net * (Number(taxPercent || 0) / 100);
    return {
      subtotal, discount, net, taxAmt, total: net + taxAmt,
    };
  }, [lineItems, taxPercent]);

  const updateLine = (key, patch) => {
    setLineItems((prev) => prev.map((li) => (li._key === key ? { ...li, ...patch } : li)));
  };

  const removeLine = (key) => setLineItems((prev) => prev.filter((li) => li._key !== key));

  const availableServices = allServices.filter(
    (s) => !lineItems.some((li) => li.service_id === s.id)
  );

  const addService = (svc) => {
    setLineItems((prev) => [...prev, svcToLine(svc)]);
    setShowPicker(false);
  };

  const save = async ({ downloadPdf }) => {
    const requiredFields = [
      ["name", "Client name"],
      ["company", "Company"],
      ["email", "Email"],
      ["phone", "Phone"],
      ["gstin", "GSTIN"],
    ];
    for (const [k, label] of requiredFields) {
      if (!(client[k] || "").trim()) {
        toast.error(`${label} is required`);
        return;
      }
    }
    // Simple email format check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email.trim())) {
      toast.error("Please enter a valid email");
      return;
    }
    if (lineItems.length === 0) { toast.error("Add at least one service"); return; }
    setSaving(true);
    try {
      const payload = {
        client,
        line_items: lineItems.map(({ _key, ...rest }) => rest),
        tax_percent: Number(taxPercent),
        valid_until: validUntil,
        notes,
        currency: "INR",
      };
      const { data } = await api.post("/quotes", payload);
      toast.success(`Quote ${data.quote_number} created`);
      if (downloadPdf) {
        await generateQuotePdf(data);
      }
      nav("/quotes");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas" data-testid="quote-builder-page">
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => nav(-1)}
            className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-brand transition-colors font-medium"
            data-testid="quote-back-btn"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div className="text-xs uppercase tracking-[0.3em] text-brand font-bold flex items-center gap-2">
            <Receipt size={16} weight="bold" /> New Quote
          </div>
        </div>

        <div className="mb-6 animate-fade-up">
          <h1 className="font-display font-black text-zinc-900 text-4xl sm:text-5xl leading-[0.95] tracking-tight">
            Build a <span className="text-brand italic">winning quote.</span>
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl">
            Pick services, tweak quantities and discounts, add client details — download a
            beautifully branded PDF in one click.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          {/* Left column: line items */}
          <div className="space-y-6">
            <section className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-zinc-200 bg-cream flex items-center justify-between">
                <h2 className="font-display font-bold text-zinc-900 text-sm uppercase tracking-widest">Line Items</h2>
                <div className="relative">
                  <Button
                    size="sm"
                    onClick={() => setShowPicker((v) => !v)}
                    data-testid="add-service-to-quote-btn"
                    className="bg-brand hover:bg-brand-hover text-white gap-1"
                  >
                    + Add Service <CaretDown size={12} weight="bold" />
                  </Button>
                  {showPicker && (
                    <div className="absolute z-40 right-0 mt-2 w-96 max-h-96 overflow-auto bg-white border border-zinc-200 rounded-md shadow-xl">
                      {availableServices.length === 0 && (
                        <div className="p-4 text-sm text-zinc-500">All services already added.</div>
                      )}
                      {availableServices.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => addService(s)}
                          data-testid={`picker-${s.id}`}
                          className="w-full text-left px-3 py-2.5 hover:bg-brand-soft transition-colors border-b border-zinc-100 last:border-0"
                        >
                          <div className="text-sm text-zinc-900 font-medium">{s.service_name}</div>
                          <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
                            <span>{s.category}</span>·<span className="font-mono-num text-brand font-semibold">{INR.format(s.price_per_unit || 0)}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {lineItems.length === 0 ? (
                <div className="text-center py-16 text-zinc-400 text-sm">
                  <Storefront size={40} className="mx-auto mb-3 text-zinc-300" />
                  No services yet. Click <span className="font-semibold text-brand">+ Add Service</span> to start.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold bg-zinc-50/70 border-b border-zinc-200">
                        <th className="text-left px-4 py-3">Service</th>
                        <th className="text-right px-3 py-3 w-28">Unit Price</th>
                        <th className="text-right px-3 py-3 w-24">Qty</th>
                        <th className="text-right px-3 py-3 w-24">Disc %</th>
                        <th className="text-right px-3 py-3 w-32">Line Total</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((li) => {
                        const gross = Number(li.unit_price || 0) * Number(li.quantity || 0);
                        const disc = gross * (Number(li.discount_percent || 0) / 100);
                        const lineTotal = gross - disc;
                        return (
                          <tr key={li._key} data-testid={`line-${li._key}`} className="border-b border-zinc-100">
                            <td className="px-4 py-3">
                              <div className="font-semibold text-zinc-900">{li.service_name}</div>
                              <div className="text-xs text-zinc-500 mt-0.5">
                                {li.category} · {li.duration} · {li.moq}
                              </div>
                              {li.cities?.length > 0 && (
                                <div className="mt-1.5 flex flex-wrap gap-1">
                                  {li.cities.map((c, i) => <span key={i} className="city-chip">{c}</span>)}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <Input
                                type="number"
                                value={li.unit_price}
                                onChange={(e) => updateLine(li._key, { unit_price: Number(e.target.value || 0) })}
                                data-testid={`line-${li._key}-unit`}
                                className="h-9 text-right bg-white border-zinc-300 font-mono-num"
                              />
                            </td>
                            <td className="px-3 py-3">
                              <Input
                                type="number"
                                min="0"
                                value={li.quantity}
                                onChange={(e) => updateLine(li._key, { quantity: Number(e.target.value || 0) })}
                                data-testid={`line-${li._key}-qty`}
                                className="h-9 text-right bg-white border-zinc-300 font-mono-num"
                              />
                            </td>
                            <td className="px-3 py-3">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={li.discount_percent}
                                onChange={(e) => updateLine(li._key, { discount_percent: Number(e.target.value || 0) })}
                                data-testid={`line-${li._key}-disc`}
                                className="h-9 text-right bg-white border-zinc-300 font-mono-num"
                              />
                            </td>
                            <td className="px-3 py-3 text-right font-mono-num font-bold text-brand">
                              {INR.format(lineTotal)}
                            </td>
                            <td className="px-2 py-3 text-center">
                              <button
                                onClick={() => removeLine(li._key)}
                                data-testid={`line-${li._key}-remove`}
                                className="text-zinc-400 hover:text-red-600 p-1 rounded transition-colors"
                                aria-label="Remove line"
                              >
                                <Trash size={16} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Notes */}
            <section className="rounded-lg border border-zinc-200 bg-white shadow-sm p-5">
              <Label htmlFor="notes" className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-bold">
                Notes / Terms
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. 50% advance to confirm booking. Rates inclusive of production."
                data-testid="quote-notes"
                className="mt-2 bg-white border-zinc-300 text-zinc-900 min-h-[100px]"
              />
            </section>
          </div>

          {/* Right column: client + totals */}
          <aside className="space-y-6">
            <section className="rounded-lg border border-zinc-200 bg-white shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <User size={18} weight="duotone" className="text-brand" />
                <h2 className="font-display font-bold text-zinc-900 text-sm uppercase tracking-widest">Client</h2>
              </div>
              <div className="space-y-3">
                <ClientField label="Name *" v={client.name} onChange={(v) => setClient({ ...client, name: v })} testid="c-name" required />
                <ClientField label="Company *" v={client.company} onChange={(v) => setClient({ ...client, company: v })} testid="c-company" required />
                <ClientField label="Email *" v={client.email} onChange={(v) => setClient({ ...client, email: v })} testid="c-email" required type="email" />
                <ClientField label="Phone *" v={client.phone} onChange={(v) => setClient({ ...client, phone: v })} testid="c-phone" required />
                <ClientField label="GSTIN *" v={client.gstin} onChange={(v) => setClient({ ...client, gstin: v })} testid="c-gstin" required />
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Address</Label>
                  <Textarea
                    value={client.address}
                    onChange={(e) => setClient({ ...client, address: e.target.value })}
                    data-testid="c-address"
                    className="bg-white border-zinc-300 text-zinc-900 min-h-[64px] text-sm"
                  />
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-zinc-200 bg-white shadow-sm p-5">
              <h2 className="font-display font-bold text-zinc-900 text-sm uppercase tracking-widest mb-4">Terms</h2>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Tax %</Label>
                    <Input
                      type="number"
                      value={taxPercent}
                      onChange={(e) => setTaxPercent(Number(e.target.value || 0))}
                      data-testid="tax-percent"
                      className="bg-white border-zinc-300 font-mono-num text-right"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Valid Until</Label>
                    <Input
                      type="date"
                      value={validUntil}
                      onChange={(e) => setValidUntil(e.target.value)}
                      data-testid="valid-until"
                      className="bg-white border-zinc-300"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden" data-testid="totals-card">
              <div className="p-5 space-y-2">
                <TotalRow k="Subtotal" v={INR.format(totals.subtotal)} />
                <TotalRow k="Discount" v={`− ${INR.format(totals.discount)}`} muted />
                <TotalRow k="Net" v={INR.format(totals.net)} />
                <TotalRow k={`Tax (${taxPercent || 0}%)`} v={INR.format(totals.taxAmt)} muted />
              </div>
              <div className="bg-brand text-white px-5 py-4 flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest opacity-90 font-bold">Grand Total</div>
                <div className="font-display font-black text-2xl font-mono-num" data-testid="grand-total">
                  {INR.format(totals.total)}
                </div>
              </div>
            </section>

            <div className="flex flex-col gap-2">
              <Button
                onClick={() => save({ downloadPdf: true })}
                disabled={saving}
                data-testid="save-and-pdf-btn"
                className="bg-brand hover:bg-brand-hover text-white h-11 gap-2 shadow-sm"
              >
                <FilePdf size={18} weight="bold" />
                {saving ? "Saving..." : "Save & Download PDF"}
              </Button>
              <Button
                onClick={() => save({ downloadPdf: false })}
                disabled={saving}
                variant="outline"
                data-testid="save-draft-btn"
                className="bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50 h-11 gap-2"
              >
                <FloppyDisk size={18} weight="bold" /> Save Only
              </Button>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function ClientField({ label, v, onChange, testid, required = false, type = "text" }) {
  const missing = required && !(v || "").trim();
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">{label}</Label>
      <Input
        type={type}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        aria-invalid={missing}
        className={`bg-white text-zinc-900 text-sm h-9 ${missing ? "border-red-300 focus-visible:ring-red-300" : "border-zinc-300"}`}
      />
    </div>
  );
}

function TotalRow({ k, v, muted }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={muted ? "text-zinc-500" : "text-zinc-700"}>{k}</span>
      <span className={`font-mono-num ${muted ? "text-zinc-600" : "text-zinc-900 font-semibold"}`}>{v}</span>
    </div>
  );
}
